import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { usePrescriptionSettings, useUpdatePrescriptionSettings } from '../../hooks/usePrescriptionSettings';
import { usePrinters } from '../../hooks/usePrinters';
import { PageHeader } from '../../components/common/PageHeader';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { UpdatePrescriptionSettingsDto, PrintType } from '../../types/settings.types';
import { usePrint } from '../../hooks/usePrint';
import {
  saveSettingsToLocal,
  getSettingsFromLocal,
  exportSettings,
  importSettings,
} from '../../utils/localStorageSettings';
import toast from 'react-hot-toast';
import { printApi } from '../../api/print.api';

// Standard paper sizes in millimeters
const PAPER_SIZES = {
  A4: { width: 210, height: 297, label: 'A4 (210 × 297 mm)' },
  A5: { width: 148, height: 210, label: 'A5 (148 × 210 mm)' },
  LETTER: { width: 216, height: 279, label: 'Letter (216 × 279 mm)' },
  LEGAL: { width: 216, height: 356, label: 'Legal (216 × 356 mm)' },
  CUSTOM: { width: 0, height: 0, label: 'مخصص (Custom)' },
};

type TabType = 'basic' | 'layout' | 'print' | 'preview';

export const PrescriptionSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const { data: settings, isLoading, error } = usePrescriptionSettings();
  const { data: printers, isLoading: printersLoading, refetch: refetchPrinters } = usePrinters();
  const updateMutation = useUpdatePrescriptionSettings();
  const printMutation = usePrint();

  const [formData, setFormData] = React.useState<UpdatePrescriptionSettingsDto | null>(null);
  const [testPrescriptionId, setTestPrescriptionId] = React.useState<string>('1');
  const [selectedPaperSize, setSelectedPaperSize] = React.useState<string>('CUSTOM');
  const [activeTab, setActiveTab] = React.useState<TabType>('basic');
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = React.useState(false);
  const previewUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (settings) {
      saveSettingsToLocal(settings);
      
      setFormData({
        clinicName: settings.clinicName,
        clinicAddress: settings.clinicAddress,
        clinicPhone: settings.clinicPhone,
        doctorName: settings.doctorName,
        paperWidthMM: settings.paperWidthMM,
        paperHeightMM: settings.paperHeightMM,
        marginTop: settings.marginTop,
        marginBottom: settings.marginBottom,
        marginLeft: settings.marginLeft,
        marginRight: settings.marginRight,
        fontName: settings.fontName,
        fontSize: settings.fontSize,
        printWithLogo: settings.printWithLogo,
        logoUrl: settings.logoUrl,
        printType: settings.printType,
        defaultPrinterName: settings.defaultPrinterName,
        autoPrint: settings.autoPrint,
        showHeader: settings.showHeader,
        showDiagnosis: settings.showDiagnosis,
        showFooter: settings.showFooter,
        showQR: settings.showQR,
        headerTop: settings.headerTop ?? 0,
        headerLeft: settings.headerLeft ?? 0,
        doctorNameTop: settings.doctorNameTop ?? 0,
        doctorNameLeft: settings.doctorNameLeft ?? 0,
        diagnosisTop: settings.diagnosisTop ?? 0,
        diagnosisLeft: settings.diagnosisLeft ?? 0,
        medicationsTop: settings.medicationsTop ?? 0,
        medicationsLeft: settings.medicationsLeft ?? 0,
        footerTop: settings.footerTop ?? 0,
        footerLeft: settings.footerLeft ?? 0,
        qrTop: settings.qrTop ?? 0,
        qrLeft: settings.qrLeft ?? 0,
      });

      const currentSize = Object.entries(PAPER_SIZES).find(
        ([_, size]) =>
          size.width === settings.paperWidthMM && size.height === settings.paperHeightMM
      );
      if (currentSize) {
        setSelectedPaperSize(currentSize[0]);
      } else {
        setSelectedPaperSize('CUSTOM');
      }
    } else {
      const localSettings = getSettingsFromLocal();
      if (localSettings) {
        toast(t('settings.loadedFromLocal', 'تم تحميل الإعدادات من النسخة المحلية'), {
          icon: 'ℹ️',
        });
        setFormData({
          clinicName: localSettings.clinicName,
          clinicAddress: localSettings.clinicAddress,
          clinicPhone: localSettings.clinicPhone,
          doctorName: localSettings.doctorName,
          paperWidthMM: localSettings.paperWidthMM,
          paperHeightMM: localSettings.paperHeightMM,
          marginTop: localSettings.marginTop,
          marginBottom: localSettings.marginBottom,
          marginLeft: localSettings.marginLeft,
          marginRight: localSettings.marginRight,
          fontName: localSettings.fontName,
          fontSize: localSettings.fontSize,
          printWithLogo: localSettings.printWithLogo,
          logoUrl: localSettings.logoUrl,
          printType: localSettings.printType,
          defaultPrinterName: localSettings.defaultPrinterName,
          autoPrint: localSettings.autoPrint,
          showHeader: localSettings.showHeader,
          showDiagnosis: localSettings.showDiagnosis,
          showFooter: localSettings.showFooter,
          showQR: localSettings.showQR,
          headerTop: (localSettings as any).headerTop ?? 0,
          headerLeft: (localSettings as any).headerLeft ?? 0,
          doctorNameTop: (localSettings as any).doctorNameTop ?? 0,
          doctorNameLeft: (localSettings as any).doctorNameLeft ?? 0,
          diagnosisTop: (localSettings as any).diagnosisTop ?? 0,
          diagnosisLeft: (localSettings as any).diagnosisLeft ?? 0,
          medicationsTop: (localSettings as any).medicationsTop ?? 0,
          medicationsLeft: (localSettings as any).medicationsLeft ?? 0,
          footerTop: (localSettings as any).footerTop ?? 0,
          footerLeft: (localSettings as any).footerLeft ?? 0,
          qrTop: (localSettings as any).qrTop ?? 0,
          qrLeft: (localSettings as any).qrLeft ?? 0,
        });
      }
    }
  }, [settings, t]);

  const generatePreview = React.useCallback(async () => {
    const id = parseInt(testPrescriptionId, 10);
    if (Number.isNaN(id) || id <= 0) {
      if (previewUrlRef.current?.startsWith('blob:')) {
        window.URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }

    setIsGeneratingPreview(true);
    if (previewUrlRef.current?.startsWith('blob:')) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    try {
      const objectUrl = await printApi.getPrescriptionPdfForPreview(id);
      previewUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error(t('settings.previewError', 'فشل في توليد المعاينة'));
    } finally {
      setIsGeneratingPreview(false);
    }
  }, [testPrescriptionId, t]);

  // Generate preview when test prescription ID or form data changes
  React.useEffect(() => {
    if (testPrescriptionId && formData) {
      generatePreview();
    }
  }, [testPrescriptionId, formData, generatePreview]);

  // Revoke blob URL on unmount to avoid memory leaks
  React.useEffect(() => {
    return () => {
      if (previewUrlRef.current?.startsWith('blob:')) {
        window.URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData) {
      try {
        const updatedSettings = await updateMutation.mutateAsync(formData);
        if (updatedSettings) {
          saveSettingsToLocal(updatedSettings);
        }
        toast.success(t('settings.settingsSaved', 'تم حفظ الإعدادات بنجاح'));
        // Regenerate preview after save
        if (testPrescriptionId) {
          generatePreview();
        }
      } catch (error) {
        if (settings && formData) {
          const localSettings = {
            ...settings,
            ...formData,
          };
          saveSettingsToLocal(localSettings);
          toast(
            t('settings.settingsSavedLocally', 'تم حفظ الإعدادات محلياً فقط. السيرفر غير متاح.'),
            { icon: '⚠️' }
          );
        } else {
          toast.error(t('settings.settingsSaveError', 'فشل في حفظ الإعدادات'));
        }
      }
    }
  };

  const handleExport = async () => {
    if (settings) {
      try {
        await exportSettings(settings);
        toast.success(t('settings.settingsExported', 'تم تصدير الإعدادات بنجاح'));
      } catch (error) {
        toast.error(t('settings.settingsExportError', 'فشل في تصدير الإعدادات'));
      }
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const importedSettings = await importSettings(file);
      setFormData({
        clinicName: importedSettings.clinicName,
        clinicAddress: importedSettings.clinicAddress,
        clinicPhone: importedSettings.clinicPhone,
        doctorName: importedSettings.doctorName,
        paperWidthMM: importedSettings.paperWidthMM,
        paperHeightMM: importedSettings.paperHeightMM,
        marginTop: importedSettings.marginTop,
        marginBottom: importedSettings.marginBottom,
        marginLeft: importedSettings.marginLeft,
        marginRight: importedSettings.marginRight,
        fontName: importedSettings.fontName,
        fontSize: importedSettings.fontSize,
        printWithLogo: importedSettings.printWithLogo,
        logoUrl: importedSettings.logoUrl,
        printType: importedSettings.printType,
        defaultPrinterName: importedSettings.defaultPrinterName,
        autoPrint: importedSettings.autoPrint,
        showHeader: importedSettings.showHeader,
        showDiagnosis: importedSettings.showDiagnosis,
        showFooter: importedSettings.showFooter,
        showQR: importedSettings.showQR,
        headerTop: (importedSettings as any).headerTop ?? 0,
        headerLeft: (importedSettings as any).headerLeft ?? 0,
        doctorNameTop: (importedSettings as any).doctorNameTop ?? 0,
        doctorNameLeft: (importedSettings as any).doctorNameLeft ?? 0,
        diagnosisTop: (importedSettings as any).diagnosisTop ?? 0,
        diagnosisLeft: (importedSettings as any).diagnosisLeft ?? 0,
        medicationsTop: (importedSettings as any).medicationsTop ?? 0,
        medicationsLeft: (importedSettings as any).medicationsLeft ?? 0,
        footerTop: (importedSettings as any).footerTop ?? 0,
        footerLeft: (importedSettings as any).footerLeft ?? 0,
        qrTop: (importedSettings as any).qrTop ?? 0,
        qrLeft: (importedSettings as any).qrLeft ?? 0,
      });
      
      saveSettingsToLocal(importedSettings);
      
      try {
        await updateMutation.mutateAsync({
          clinicName: importedSettings.clinicName,
          clinicAddress: importedSettings.clinicAddress,
          clinicPhone: importedSettings.clinicPhone,
          doctorName: importedSettings.doctorName,
          paperWidthMM: importedSettings.paperWidthMM,
          paperHeightMM: importedSettings.paperHeightMM,
          marginTop: importedSettings.marginTop,
          marginBottom: importedSettings.marginBottom,
          marginLeft: importedSettings.marginLeft,
          marginRight: importedSettings.marginRight,
          fontName: importedSettings.fontName,
          fontSize: importedSettings.fontSize,
          printWithLogo: importedSettings.printWithLogo,
          logoUrl: importedSettings.logoUrl,
          printType: importedSettings.printType,
          defaultPrinterName: importedSettings.defaultPrinterName,
          autoPrint: importedSettings.autoPrint,
          showHeader: importedSettings.showHeader,
          showDiagnosis: importedSettings.showDiagnosis,
          showFooter: importedSettings.showFooter,
          showQR: importedSettings.showQR,
          headerTop: (importedSettings as any).headerTop ?? 0,
          headerLeft: (importedSettings as any).headerLeft ?? 0,
          doctorNameTop: (importedSettings as any).doctorNameTop ?? 0,
          doctorNameLeft: (importedSettings as any).doctorNameLeft ?? 0,
          diagnosisTop: (importedSettings as any).diagnosisTop ?? 0,
          diagnosisLeft: (importedSettings as any).diagnosisLeft ?? 0,
          medicationsTop: (importedSettings as any).medicationsTop ?? 0,
          medicationsLeft: (importedSettings as any).medicationsLeft ?? 0,
          footerTop: (importedSettings as any).footerTop ?? 0,
          footerLeft: (importedSettings as any).footerLeft ?? 0,
          qrTop: (importedSettings as any).qrTop ?? 0,
          qrLeft: (importedSettings as any).qrLeft ?? 0,
        });
        toast.success(t('settings.settingsImported', 'تم استيراد الإعدادات بنجاح'));
      } catch (error) {
        toast(
          t('settings.settingsImportedLocally', 'تم استيراد الإعدادات محلياً فقط. السيرفر غير متاح.'),
          { icon: '⚠️' }
        );
      }
    } catch (error) {
      toast.error(t('settings.settingsImportError', 'فشل في استيراد الإعدادات. تأكد من صحة الملف.'));
    }
    
    e.target.value = '';
  };

  const handlePaperSizeChange = (sizeKey: string) => {
    setSelectedPaperSize(sizeKey);
    if (sizeKey !== 'CUSTOM' && formData) {
      const size = PAPER_SIZES[sizeKey as keyof typeof PAPER_SIZES];
      setFormData({
        ...formData,
        paperWidthMM: size.width,
        paperHeightMM: size.height,
      });
    }
  };

  const handleLoadPrinters = async () => {
    try {
      await refetchPrinters();
      toast.success(t('settings.printersLoaded', 'تم تحميل الطابعات بنجاح'));
    } catch (error) {
      toast.error(t('settings.printersLoadError', 'فشل في تحميل الطابعات'));
    }
  };

  const printTypeOptions: { value: PrintType; label: string }[] = [
    { value: 'PDF', label: 'PDF' },
    { value: 'THERMAL', label: t('settings.thermal', 'Thermal') },
    { value: 'RAW', label: t('settings.raw', 'Raw') },
  ];

  if (isLoading) {
    return <LoadingSpinner size="lg" className="py-12" />;
  }

  if (error || !settings || !formData) {
    return <ErrorMessage message={error?.message || 'Failed to load settings'} />;
  }

  return (
    <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader title={t('settings.prescriptionSettings', 'Prescription Settings')} />

      {/* Tabs Navigation */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-0">
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'basic' ? 'active' : ''}`}
                onClick={() => setActiveTab('basic')}
                type="button"
              >
                {t('settings.basicSettings', 'الإعدادات الأساسية')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'layout' ? 'active' : ''}`}
                onClick={() => setActiveTab('layout')}
                type="button"
              >
                {t('settings.layoutPositions', 'التخطيط والمواضع')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'print' ? 'active' : ''}`}
                onClick={() => setActiveTab('print')}
                type="button"
              >
                {t('settings.printSettings', 'إعدادات الطباعة')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'preview' ? 'active' : ''}`}
                onClick={() => setActiveTab('preview')}
                type="button"
              >
                {t('settings.fullPreview', 'معاينة كاملة')}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Settings Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-4">
            {/* Clinic Information */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h2 className="h5 fw-bold mb-4">{t('settings.clinicInformation', 'Clinic Information')}</h2>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Input
                      label={t('settings.clinicName', 'Clinic Name')}
                      value={formData.clinicName}
                      onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <Input
                      label={t('settings.doctorName', 'Doctor Name')}
                      value={formData.doctorName}
                      onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <Input
                      label={t('settings.clinicAddress', 'Clinic Address')}
                      value={formData.clinicAddress || ''}
                      onChange={(e) => setFormData({ ...formData, clinicAddress: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <Input
                      label={t('settings.clinicPhone', 'Clinic Phone')}
                      value={formData.clinicPhone || ''}
                      onChange={(e) => setFormData({ ...formData, clinicPhone: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Paper Settings */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h2 className="h5 fw-bold mb-4">{t('settings.paperSettings', 'إعدادات الورق')}</h2>
                
                <div className="mb-4">
                  <label className="form-label fw-semibold mb-2">
                    {t('settings.paperSize', 'حجم الورق')}
                  </label>
                  <Select
                    value={selectedPaperSize}
                    onChange={(value: string | number) => handlePaperSizeChange(String(value))}
                    options={Object.entries(PAPER_SIZES).map(([key, size]) => ({
                      value: key,
                      label: size.label,
                    }))}
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <Input
                      label={t('settings.width', 'العرض') + ' (mm)'}
                      type="number"
                      step="0.1"
                      min="50"
                      max="500"
                      value={formData.paperWidthMM}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value) && value > 0) {
                          setFormData({ ...formData, paperWidthMM: value });
                          setSelectedPaperSize('CUSTOM');
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <Input
                      label={t('settings.height', 'الارتفاع') + ' (mm)'}
                      type="number"
                      step="0.1"
                      min="50"
                      max="500"
                      value={formData.paperHeightMM}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value) && value > 0) {
                          setFormData({ ...formData, paperHeightMM: value });
                          setSelectedPaperSize('CUSTOM');
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="border-top pt-3 mt-3">
                  <h6 className="fw-semibold mb-3">{t('settings.margins', 'الهوامش')}</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        label={t('settings.marginTop', 'الهامش العلوي') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.marginTop}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, marginTop: value });
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t('settings.marginBottom', 'الهامش السفلي') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.marginBottom}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, marginBottom: value });
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t('settings.marginLeft', 'الهامش الأيسر') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.marginLeft}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, marginLeft: value });
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t('settings.marginRight', 'الهامش الأيمن') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.marginRight}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, marginRight: value });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Font Settings */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h2 className="h5 fw-bold mb-4">{t('settings.fontSettings', 'Font Settings')}</h2>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Input
                      label={t('settings.fontName', 'Font Name')}
                      value={formData.fontName}
                      onChange={(e) => setFormData({ ...formData, fontName: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <Input
                      label={t('settings.fontSize', 'Font Size')}
                      type="number"
                      min="6"
                      max="40"
                      value={formData.fontSize}
                      onChange={(e) =>
                        setFormData({ ...formData, fontSize: parseInt(e.target.value, 10) })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Display Settings */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h2 className="h5 fw-bold mb-4">{t('settings.displaySettings', 'Display Settings')}</h2>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.showHeader}
                        onChange={(e) =>
                          setFormData({ ...formData, showHeader: e.target.checked })
                        }
                        id="showHeader"
                      />
                      <label className="form-check-label" htmlFor="showHeader">
                        {t('settings.showHeader', 'Show Header')}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.showDiagnosis}
                        onChange={(e) =>
                          setFormData({ ...formData, showDiagnosis: e.target.checked })
                        }
                        id="showDiagnosis"
                      />
                      <label className="form-check-label" htmlFor="showDiagnosis">
                        {t('settings.showDiagnosis', 'Show Diagnosis')}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.showFooter}
                        onChange={(e) =>
                          setFormData({ ...formData, showFooter: e.target.checked })
                        }
                        id="showFooter"
                      />
                      <label className="form-check-label" htmlFor="showFooter">
                        {t('settings.showFooter', 'Show Footer')}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.showQR}
                        onChange={(e) => setFormData({ ...formData, showQR: e.target.checked })}
                        id="showQR"
                      />
                      <label className="form-check-label" htmlFor="showQR">
                        {t('settings.showQR', 'Show QR Code')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Layout & Positions Tab */}
        {activeTab === 'layout' && (
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h2 className="h5 fw-bold mb-4">{t('settings.elementPositions', 'مواضع العناصر')}</h2>
              <p className="text-muted mb-4">
                {t('settings.positionNote', 'قم بتحديد مواضع العناصر بالملليمتر من الزاوية العلوية اليسرى')}
              </p>

              {/* Header Position */}
              {formData.showHeader && (
                <div className="border rounded p-3 mb-3">
                  <h6 className="fw-semibold mb-3">{t('settings.headerPosition', 'موضع العنوان')}</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        label={t('settings.top', 'من الأعلى') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.headerTop}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, headerTop: value });
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t('settings.left', 'من اليسار') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.headerLeft}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, headerLeft: value });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Doctor Name Position */}
              <div className="border rounded p-3 mb-3">
                <h6 className="fw-semibold mb-3">{t('settings.doctorNamePosition', 'موضع اسم الطبيب')}</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Input
                      label={t('settings.top', 'من الأعلى') + ' (mm)'}
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.doctorNameTop}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value) && value >= 0) {
                          setFormData({ ...formData, doctorNameTop: value });
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <Input
                      label={t('settings.left', 'من اليسار') + ' (mm)'}
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.doctorNameLeft}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value) && value >= 0) {
                          setFormData({ ...formData, doctorNameLeft: value });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Diagnosis Position */}
              {formData.showDiagnosis && (
                <div className="border rounded p-3 mb-3">
                  <h6 className="fw-semibold mb-3">{t('settings.diagnosisPosition', 'موضع التشخيص')}</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        label={t('settings.top', 'من الأعلى') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.diagnosisTop}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, diagnosisTop: value });
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t('settings.left', 'من اليسار') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.diagnosisLeft}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, diagnosisLeft: value });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Medications Position */}
              <div className="border rounded p-3 mb-3">
                <h6 className="fw-semibold mb-3">{t('settings.medicationsPosition', 'موضع الأدوية')}</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Input
                      label={t('settings.top', 'من الأعلى') + ' (mm)'}
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.medicationsTop}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value) && value >= 0) {
                          setFormData({ ...formData, medicationsTop: value });
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <Input
                      label={t('settings.left', 'من اليسار') + ' (mm)'}
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.medicationsLeft}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value) && value >= 0) {
                          setFormData({ ...formData, medicationsLeft: value });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Footer Position */}
              {formData.showFooter && (
                <div className="border rounded p-3 mb-3">
                  <h6 className="fw-semibold mb-3">{t('settings.footerPosition', 'موضع التذييل')}</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        label={t('settings.top', 'من الأعلى') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.footerTop}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, footerTop: value });
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t('settings.left', 'من اليسار') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.footerLeft}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, footerLeft: value });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* QR Code Position */}
              {formData.showQR && (
                <div className="border rounded p-3 mb-3">
                  <h6 className="fw-semibold mb-3">{t('settings.qrPosition', 'موضع رمز QR')}</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        label={t('settings.top', 'من الأعلى') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.qrTop}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, qrTop: value });
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t('settings.left', 'من اليسار') + ' (mm)'}
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.qrLeft}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!Number.isNaN(value) && value >= 0) {
                            setFormData({ ...formData, qrLeft: value });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Print Settings Tab */}
        {activeTab === 'print' && (
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h2 className="h5 fw-bold mb-4">{t('settings.printSettings', 'Print Settings')}</h2>
              
              <div className="mb-4">
                <Select
                  label={t('settings.printType', 'Print Type')}
                  options={printTypeOptions}
                  value={formData.printType}
                  onChange={(value: string | number) =>
                    setFormData({ ...formData, printType: value as PrintType })
                  }
                />
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-semibold mb-0">
                    {t('settings.defaultPrinter', 'الطابعة الافتراضية')}
                  </label>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleLoadPrinters}
                    isLoading={printersLoading}
                  >
                    {t('settings.loadPrinters', 'تحميل الطابعات')}
                  </Button>
                </div>
                <Select
                  value={formData.defaultPrinterName || ''}
                  onChange={(value: string | number) =>
                    setFormData({ ...formData, defaultPrinterName: String(value) || undefined })
                  }
                  options={[
                    { value: '', label: t('settings.selectPrinter', 'اختر طابعة...') },
                    ...(printers || []).map((printer) => ({
                      value: printer,
                      label: printer,
                    })),
                  ]}
                />
                {printers && printers.length === 0 && (
                  <small className="text-muted d-block mt-2">
                    {t('settings.noPrintersFound', 'لم يتم العثور على طابعات. اضغط على "تحميل الطابعات"')}
                  </small>
                )}
              </div>

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formData.autoPrint}
                  onChange={(e) => setFormData({ ...formData, autoPrint: e.target.checked })}
                  id="autoPrint"
                />
                <label className="form-check-label" htmlFor="autoPrint">
                  {t('settings.autoPrint', 'Auto Print')}
                </label>
              </div>

              <div className="border-top pt-3 mt-3">
                <h6 className="fw-semibold mb-3">{t('settings.logoSettings', 'إعدادات الشعار')}</h6>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.printWithLogo}
                    onChange={(e) =>
                      setFormData({ ...formData, printWithLogo: e.target.checked })
                    }
                    id="printWithLogo"
                  />
                  <label className="form-check-label" htmlFor="printWithLogo">
                    {t('settings.printWithLogo', 'Print With Logo')}
                  </label>
                </div>
                {formData.printWithLogo && (
                  <Input
                    label={t('settings.logoUrl', 'Logo URL')}
                    value={formData.logoUrl || ''}
                    onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Full Preview Tab */}
        {activeTab === 'preview' && (
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h2 className="h5 fw-bold mb-4">{t('settings.fullPreview', 'معاينة كاملة')}</h2>
              
              <div className="mb-4">
                <div className="row g-3 align-items-end">
                  <div className="col-md-6">
                    <Input
                      label={t('settings.testPrescriptionId', 'Test Prescription ID')}
                      type="number"
                      value={testPrescriptionId}
                      onChange={(e) => setTestPrescriptionId(e.target.value)}
                      placeholder="1"
                    />
                  </div>
                  <div className="col-md-6">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={generatePreview}
                      isLoading={isGeneratingPreview}
                      fullWidth
                    >
                      {t('settings.generatePreview', 'توليد المعاينة')}
                    </Button>
                  </div>
                </div>
              </div>

              {previewUrl ? (
                <div className="border rounded p-3" style={{ minHeight: '600px' }}>
                  <iframe
                    src={previewUrl}
                    className="w-100"
                    style={{ height: '800px', border: 'none' }}
                    title="Prescription Preview"
                  />
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <p>{t('settings.enterPrescriptionId', 'أدخل رقم الوصفة الطبية لتوليد المعاينة')}</p>
                </div>
              )}

              <div className="mt-4 d-flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const id = parseInt(testPrescriptionId, 10);
                    if (!Number.isNaN(id) && id > 0) {
                      void printApi.downloadPrescriptionPdf(id);
                    }
                  }}
                  disabled={!testPrescriptionId || Number.isNaN(parseInt(testPrescriptionId, 10))}
                >
                  {t('settings.downloadPdf', 'تحميل PDF')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const id = parseInt(testPrescriptionId, 10);
                    if (!Number.isNaN(id) && id > 0) {
                      void printMutation.mutateAsync(id);
                    }
                  }}
                  isLoading={printMutation.isPending}
                  disabled={!testPrescriptionId || Number.isNaN(parseInt(testPrescriptionId, 10))}
                >
                  {t('settings.testPrint', 'Test Print')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <div className="d-flex gap-2 flex-wrap">
              <Button type="submit" isLoading={updateMutation.isPending}>
                {t('settings.saveSettings', 'Save Settings')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleExport}
                disabled={!settings}
              >
                {t('settings.exportSettings', 'تصدير الإعدادات')}
              </Button>
              <label className="btn btn-outline-primary">
                {t('settings.importSettings', 'استيراد الإعدادات')}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
